// ============================================================
// MOONSHINE MASTERS - worldgen.js        THE WORLD'S SHAPE (pure)
//
// Seeded (mulberry32) and dependency-free: no React, no three. The same
// seed gives the same woods on every client - free deterministic worldgen
// for co-op, zero network traffic. Lives apart from World3D.js so the
// layout can be proven in Node (see the checks in the build notes).
//   logic -> data <- renderer
// ============================================================
import { WORLD, LAYOUT, PLACES, BEAST, KIT, ERRAND, SIGHT_R, SIGHTS, applyCamp, campGrow, campLevel, YARD_UP } from "./moonshineData";

// ── seeded world gen (NO PRNG DURING RENDER - baked once) ────
export function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// THE RATIO SYSTEM (2026-10-31): the world is built for ONE camp size (+ which yard pieces are solid). setCampLevel() rebuilds GEN and DRESS
// for another one; `GEN` / `DRESS` are LIVE exports (importers see the new world). The first size gives the exact same woods as before.
// More of the woods (trees, scatter) comes with a bigger map so it never thins out - drawn AFTER the old ones on the same streams.
let BUILT = null;
const areaK = () => (WORLD.R * WORLD.R - WORLD.YARD_R * WORLD.YARD_R) / (54 * 54 - 9.5 * 9.5);
// a yard prop the BIGGER shack would bury (the sacks, the woodpile): it moves inside the shack - not drawn, not solid
const underShack = (p, yard) => !!(yard && yard.shack) && Math.hypot(p.x - LAYOUT.shack[0], p.z - LAYOUT.shack[1]) < 2.3 * ((YARD_UP.shack[yard.shack] || YARD_UP.shack[0]).s) + (p.r || 0) + 0.4;
function buildGen(yard) {
  const nodes = [];
  PLACES.forEach((p, pi) => {
    const rr = mulberry32(100 + pi);
    for (let i = 0; i < p.nodes; i++) {
      const ang = rr() * Math.PI * 2, rad = Math.sqrt(rr()) * (p.r - 0.9);
      nodes.push({
        id: `${p.id}_${i}`, place: p.id,
        x: p.x + Math.cos(ang) * rad, z: p.z + Math.sin(ang) * rad,
        ing: p.yields[Math.floor(rr() * p.yields.length)], night: !!p.night, eyes: !!p.eyes,
      });
    }
  });
  const R = mulberry32(1031);
  const trees = [];
  const TREES = Math.round(240 * areaK());
  for (let guard = 0; trees.length < TREES && guard < 6000 * areaK(); guard++) {
    const ang = R() * Math.PI * 2, rad = WORLD.YARD_R + 3.5 + R() * (WORLD.R - WORLD.YARD_R - 3);
    const x = Math.cos(ang) * rad, z = Math.sin(ang) * rad;
    if (PLACES.some((p) => Math.hypot(x - p.x, z - p.z) < p.r + 2.4)) continue;
    trees.push({ x, z, s: 0.8 + R() * 0.9, h: R() });
  }
  // THE PINES (2026-10-24, art only - positions above are untouched): which pine, how big, how thick the trunk
  const lerp = (a, b, t) => a + (b - a) * t;
  for (const t of trees) {
    const u = (t.s - 0.8) / 0.9;
    t.kind = t.s < KIT.YOUNG_BELOW ? "pine_young" : ["pine_a", "pine_b", "pine_c"][Math.floor(t.h * 3) % 3];
    t.sc = t.kind === "pine_young" ? lerp(KIT.YOUNG_SCALE[0], KIT.YOUNG_SCALE[1], u / 0.17) : lerp(KIT.PINE_SCALE[0], KIT.PINE_SCALE[1], u);
    t.ry = t.h * 37.7;
    t.r = (KIT.TRUNK_R[0] * KIT.PINE_H[t.kind] + KIT.TRUNK_R[1]) * t.sc * KIT.TRUNK_R[2];
  }
  // phantom homes: ONE GUARD PER ZONE (2026-10-25), home on its compass axis (E, N, W, S), at the far edge of its wedge. Index = zone number (see zoneOf).
  const beasts = [0, -90, 180, 90].map((deg, zone) => ({ hx: Math.cos(deg * Math.PI / 180) * BEAST.HOME_R, hz: Math.sin(deg * Math.PI / 180) * BEAST.HOME_R, zone }));
  // fixed colliders (circles): trees + the yard's furniture
  const solids = trees.map((t) => ({ x: t.x, z: t.z, r: t.r }));                // the real trunk (was 0.42 * s)
  const furniture = [
    { x: LAYOUT.still[0], z: LAYOUT.still[1], r: 1.35 },
    { x: LAYOUT.shack[0], z: LAYOUT.shack[1], r: 2.3 * ((YARD_UP.shack[yard.shack] || YARD_UP.shack[0]).s) },
    ...(yard.still2 ? [{ x: LAYOUT.still2[0], z: LAYOUT.still2[1], r: 1.35 }, { x: LAYOUT.still2[0] + 1.95, z: LAYOUT.still2[1], r: 0.5 }] : []),   // + its worm tub (like still 1's)
    { x: LAYOUT.board[0] - 0.7, z: LAYOUT.board[1], r: 0.3 },
    { x: LAYOUT.board[0] + 0.7, z: LAYOUT.board[1], r: 0.3 },
    { x: -3.4, z: -1.2, r: 0.95 },
    { x: LAYOUT.campfire[0], z: LAYOUT.campfire[1], r: 0.9 },
    // 2026-10-24, with the Blender yard: the still's worm tub sticks out east, and the new yard props are solid
    ...LAYOUT.props.filter((p) => p.r && !underShack(p, yard)).map((p) => ({ x: p.x, z: p.z, r: p.r })),
  ];
  // stand-in ruins stones
  const stones = [];
  const S = mulberry32(555), ruin = PLACES.find((p) => p.id === "ruins");
  for (let i = 0; ruin && i < 9; i++) {      // (the Old Ruins are retired 2026-10-25 - no stones unless a ruins place comes back)
    const a = (i / 9) * Math.PI * 2 + S() * 0.4, rad = ruin.r + 0.2 + S() * 1.2;
    stones.push({ x: ruin.x + Math.cos(a) * rad, z: ruin.z + Math.sin(a) * rad, s: 0.7 + S() * 1.0, ry: S() * 3 });
  }
  // THE OLD WRECK (2026-10-24): the junk car parks where it buries no scrap spot. It is solid (three circles down its
  // length). Model front = +X; three rotation.y = ry puts local +X at (cos ry, -sin ry).
  let car = null;
  const wreck = PLACES.find((p) => p.id === "wreck");
  if (wreck) {
    const wn = nodes.filter((n) => n.place === "wreck"), CR = 0.95, OFF = [-1.4, 0, 1.4];
    const circles = (x, z, ry) => OFF.map((d) => ({ x: x + d * Math.cos(ry), z: z - d * Math.sin(ry), r: CR }));
    let best = -1e9;
    for (let k = 0; k < 24; k++) {
      const a = (k / 24) * Math.PI * 2, rad = k % 2 ? 1.6 : 2.6, x = wreck.x + Math.cos(a) * rad, z = wreck.z + Math.sin(a) * rad;
      for (const ry of [a + Math.PI / 2, a + Math.PI / 2 + 0.6, a + Math.PI / 2 - 0.6]) {
        const cs = circles(x, z, ry);
        const score = Math.min(...wn.map((n) => Math.min(...cs.map((c) => Math.hypot(n.x - c.x, n.z - c.z) - c.r))));
        if (score > best) { best = score; car = { x, z, ry, circles: cs, clear: score }; }
      }
    }
  }
  const allSolids = [...solids, ...furniture, ...(car ? car.circles : [])];
  // THE UNSTABLE SIGHTS (2026-10-30): a spot on the ray at that radius, nudged (out/in, then a few degrees round) until it stands clear
  // of every trunk and outside every gather place. Pure + seeded by nothing but the data, so every client agrees. Trees never move.
  const clear = (x, z) => !allSolids.some((c) => Math.hypot(x - c.x, z - c.z) < c.r + 0.9) && !PLACES.some((p) => Math.hypot(x - p.x, z - p.z) < p.r + 1.5);
  const spotOn = (deg, rad) => {
    for (let k = 0; k < 40; k++) {
      const dr = [0, 0.8, -0.8, 1.6, -1.6][k % 5], da = [0, 3, -3, 6, -6, 9, -9, 12][Math.floor(k / 5)] * Math.PI / 180;
      const a = deg * Math.PI / 180 + da, x = Math.cos(a) * (rad + dr), z = Math.sin(a) * (rad + dr);
      if (clear(x, z)) return { x, z };
    }
    return { x: Math.cos(deg * Math.PI / 180) * rad, z: Math.sin(deg * Math.PI / 180) * rad };
  };
  const wisps = ERRAND.RAYS.map((deg) => ({ deg, ...spotOn(deg, ERRAND.R) }));
  const sights = SIGHTS.map((g) => ({ ...g, ...spotOn(g.deg, SIGHT_R + campGrow(WORLD.camp)) }));
  return { nodes, trees, beasts, solids: allSolids, stones, car, wisps, sights, camp: WORLD.camp || 0, yard };
}
export let GEN = null, DRESS = null;
// which Stray Wisp is out there now: the n-th one you've brought home decides it (personal: your own count)
export const wispAt = (n) => GEN.wisps[((Math.max(0, n | 0) * ERRAND.STEP) % GEN.wisps.length + GEN.wisps.length) % GEN.wisps.length];


export const blocked = (x, z, r = 0.3) => {
  for (let i = 0; i < GEN.solids.length; i++) {
    const s = GEN.solids[i], dx = x - s.x, dz = z - s.z, rr = s.r + r;
    if (dx * dx + dz * dz < rr * rr) return true;
  }
  return false;
};
export function slide(x, z, dx, dz) {
  let nx = x + dx, nz = z + dz;
  const d = Math.hypot(nx, nz), lim = WORLD.R - 1;
  if (d > lim) { nx *= lim / d; nz *= lim / d; }
  if (!blocked(nx, nz)) return [nx, nz];
  if (!blocked(nx, z)) return [nx, z];
  if (!blocked(x, nz)) return [x, nz];
  return [x, z];
}


// ── THE DRESSING (2026-10-24) — every kit prop the woods are dressed with, as [kind, x, z, rotY, scale, tint].
// Seeded on its OWN streams (so nodes, trees and beast homes above never move). Art only: not a collider
// except where GEN.solids says so. Read by Woods3D.jsx.
function buildDress() {
  const out = [], push = (k, x, z, ry = 0, s = 1, tint = 0) => out.push([k, x, z, ry, s, tint]);
  for (const t of GEN.trees) push(t.kind, t.x, t.z, t.ry, t.sc, Math.floor(t.h * 97));
  // the yard (the STILL is not here since 2026-10-24: it is live - Woods3D's <StillRig/> builds it from the parts you own)
  push("campfire", LAYOUT.campfire[0], LAYOUT.campfire[1], 0.4);
  push("shack", LAYOUT.shack[0], LAYOUT.shack[1], Math.atan2(-LAYOUT.shack[0], -LAYOUT.shack[1]), (YARD_UP.shack[GEN.yard.shack] || YARD_UP.shack[0]).s);   // the door faces the yard; the bigger shack (a yard upgrade) is the same model scaled
  push("contract_board", LAYOUT.board[0], LAYOUT.board[1], Math.atan2(-LAYOUT.board[0], -LAYOUT.board[1]));
  for (const p of LAYOUT.props) if (p.k && !underShack(p, GEN.yard)) push(p.k, p.x, p.z, p.ry || 0, p.s || 1);
  const LANTERNS = Math.round(LAYOUT.lanternCount * WORLD.YARD_R / 9.5);    // the same gap between lanterns at any camp size
  for (let i = 0; i < LANTERNS; i++) {
    const a = (i / LANTERNS) * Math.PI * 2;
    push("lantern", Math.cos(a) * WORLD.YARD_R, Math.sin(a) * WORLD.YARD_R, Math.PI - a);            // the lamp hangs INTO the yard
  }
  const busy = (x, z, pad) => GEN.solids.some((o) => Math.hypot(x - o.x, z - o.z) < o.r + pad);
  const Y = mulberry32(2024);
  for (let n = 0, g = 0; n < KIT.YARD_TUFTS && g < 4000; g++) {
    const a = Y() * 6.283, r = WORLD.YARD_R - 2.6 + Y() * 2.2, x = Math.cos(a) * r, z = Math.sin(a) * r;
    if (busy(x, z, 0.6)) continue;
    push("tuft", x, z, Y() * 6.283, 0.7 + Y() * 0.5, n++);
  }
  // the places
  const P = Object.fromEntries(PLACES.map((p) => [p.id, p]));
  const nearNode = (x, z, d) => GEN.nodes.some((n) => Math.hypot(x - n.x, z - n.z) < d);
  const D = KIT.PLACE_DRESS;
  const scatterIn = (pl, kind, count, seed, minR = 0, maxR = null, pad = 0.9, scale = [0.8, 1.2]) => {
    const r = mulberry32(seed);
    for (let n = 0, g = 0; n < count && g < 3000; g++) {
      const a = r() * 6.283, rad = minR + Math.sqrt(r()) * ((maxR ?? pl.r + 1.2) - minR);
      const x = pl.x + Math.cos(a) * rad, z = pl.z + Math.sin(a) * rad;
      if (nearNode(x, z, pad) || busy(x, z, 0.3)) continue;
      push(kind, x, z, r() * 6.283, scale[0] + r() * (scale[1] - scale[0]), n++);
    }
  };
  if (P.cornfield) {                                                          // corn in ROWS
    const pl = P.cornfield, [rs, ss] = D.cornfield.rows, c = mulberry32(31);
    for (let rz = -pl.r; rz <= pl.r; rz += rs) for (let rx = -pl.r; rx <= pl.r; rx += ss) {
      const x = pl.x + rx + (c() - 0.5) * 0.18, z = pl.z + rz + (c() - 0.5) * 0.12;
      if (Math.hypot(rx, rz) > pl.r + 0.4 || nearNode(x, z, 0.75)) continue;
      push(c() < 0.62 ? "corn_a" : "corn_b", x, z, c() * 6.283, 0.88 + c() * 0.24);
    }
  }
  let seed = 600;
  for (const [pid, kinds] of Object.entries(D)) {
    const pl = P[pid]; if (!pl || pid === "cornfield") continue;
    for (const [kind, count] of Object.entries(kinds)) {
      if (kind === "spring_pool") { push("spring_pool", pl.x, pl.z, 0.3, 0.62); continue; }
      if (pid === "spring" && kind === "reed") { scatterIn(pl, kind, count, seed++, pl.r * 0.55, pl.r + 0.8, 0.6); continue; }
      scatterIn(pl, kind, count, seed++);
    }
  }
  if (GEN.car) push("junk_car", GEN.car.x, GEN.car.z, GEN.car.ry, 1);
  if (P.ruins) GEN.stones.forEach((st, i) => push(["ruin_a", "ruin_b", "ruin_c", "ruin_d"][i % 4], st.x, st.z, st.ry, 0.75 + 0.1 * (i % 3)));
  // the woods at large: between the yard and the edge, clear of every gathering place
  const Wr = mulberry32(4711);
  for (const [kind, count0] of Object.entries(KIT.WOODS)) {
    const count = Math.round(count0 * areaK());
    for (let n = 0, g = 0; n < count && g < 8000 * areaK(); g++) {
      const a = Wr() * 6.283, rad = WORLD.YARD_R + 1.5 + Math.sqrt(Wr()) * (WORLD.R + 6 - WORLD.YARD_R - 1.5);
      const x = Math.cos(a) * rad, z = Math.sin(a) * rad;
      if (PLACES.some((p) => Math.hypot(x - p.x, z - p.z) < p.r + 1.5)) continue;
      const big = kind === "log" || kind === "rock" || kind === "stump";
      if (busy(x, z, big ? 0.8 : 0.15)) continue;
      push(kind, x, z, Wr() * 6.283, (kind === "rock" ? 0.6 : 0.8) + Wr() * 0.6, n++);
    }
  }
  return out;
}
// which world is built: (camp size, shack size, second still). Same key = nothing rebuilt. Returns true when it changed.
const CACHE = new Map();
export function setCampLevel(level, yard = {}) {
  const lv = campLevel(level), y = { shack: yard.shack ? 1 : 0, still2: !!yard.still2 }, key = `${lv}|${y.shack}|${y.still2 ? 1 : 0}`;
  if (BUILT === key) return false;
  applyCamp(lv);
  if (!CACHE.has(key)) { GEN = buildGen(y); DRESS = buildDress(); CACHE.set(key, { GEN, DRESS }); } else ({ GEN, DRESS } = CACHE.get(key));
  BUILT = key;
  return true;
}
export const campKey = () => BUILT;
setCampLevel(0);
